import { useState, useEffect } from 'react';
import { pageService } from '../services/pageService';

/**
 * Hook to fetch CMS content for a page
 * Returns the CMS content if available, otherwise returns null
 * The page can use this to override hardcoded content with CMS content
 */
export const useCMSContent = (pageSlug) => {
  const [cmsContent, setCmsContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        console.log(`[CMS] Fetching content for page: ${pageSlug}`);
        const content = await pageService.getPageContent(pageSlug);
        console.log(`[CMS] Content received for ${pageSlug}:`, content ? 'Found' : 'Not found');
        
        if (content) {
          console.log(`[CMS] Page "${pageSlug}" has ${content.sections?.length || 0} sections`);
          // Log section details for debugging
          content.sections?.forEach((s, i) => {
            console.log(`[CMS] Section ${i}: id="${s.id}", type="${s.type}", title="${s.title}", has content="${!!s.content}", has description="${!!s.description}"`);
          });
        }
        
        setCmsContent(content);
        
        // Apply SEO from CMS if available
        if (content?.seo) {
          if (content.seo.metaTitle) {
            document.title = content.seo.metaTitle;
          }
          if (content.seo.metaDescription) {
            const metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc) metaDesc.setAttribute('content', content.seo.metaDescription);
          }
          if (content.seo.ogTitle) {
            let ogTitle = document.querySelector('meta[property="og:title"]');
            if (!ogTitle) {
              ogTitle = document.createElement('meta');
              ogTitle.setAttribute('property', 'og:title');
              document.head.appendChild(ogTitle);
            }
            ogTitle.setAttribute('content', content.seo.ogTitle);
          }
          if (content.seo.ogDescription) {
            let ogDesc = document.querySelector('meta[property="og:description"]');
            if (!ogDesc) {
              ogDesc = document.createElement('meta');
              ogDesc.setAttribute('property', 'og:description');
              document.head.appendChild(ogDesc);
            }
            ogDesc.setAttribute('content', content.seo.ogDescription);
          }
        }
      } catch (err) {
        console.log(`[CMS] Content not available for ${pageSlug}:`, err.message || err);
        console.log(`[CMS] Using hardcoded fallback content for ${pageSlug}`);
        setError(err);
        setCmsContent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [pageSlug]);

  // Helper function to get a section by ID or type
  const getSection = (idOrType) => {
    if (!cmsContent?.sections) return null;
    return cmsContent.sections.find(s => s.id === idOrType || s.type === idOrType);
  };

  // Helper function to get section content with fallback
  // Only returns CMS content if it has actual value (not empty string)
  // Priority: requested field -> description -> content -> fallback
  const getSectionContent = (idOrType, field, fallback) => {
    const section = getSection(idOrType);
    if (!section) return fallback;
    
    // Check the requested field first - only use if it has actual content
    if (section[field] && section[field].trim && section[field].trim() !== '') {
      return section[field];
    }
    if (section[field] && typeof section[field] !== 'string') {
      return section[field]; // For non-string values like arrays
    }
    
    // For 'content' field, also check 'description' as fallback (Rich Text Editor saves to description)
    if (field === 'content') {
      if (section.description && section.description.trim && section.description.trim() !== '') {
        return section.description;
      }
    }
    
    // For 'description' field, also check 'content' as fallback
    if (field === 'description') {
      if (section.content && section.content.trim && section.content.trim() !== '') {
        return section.content;
      }
    }
    
    // Return the hardcoded fallback if CMS has no content
    return fallback;
  };

  // Helper function to get items from a section
  // Returns empty array if no items, allowing fallback logic in components
  const getSectionItems = (idOrType) => {
    const section = getSection(idOrType);
    // Only return CMS items if they exist and have content
    if (section?.items && Array.isArray(section.items) && section.items.length > 0) {
      return section.items;
    }
    return []; // Return empty array so component can use its fallback
  };

  return {
    cmsContent,
    loading,
    error,
    getSection,
    getSectionContent,
    getSectionItems,
    hasCMSContent: !!cmsContent
  };
};

export default useCMSContent;
